<template>
  <div class="border border-gray-300 rounded-lg p-6 w-1/2 mx-auto">
    <h3 class="text-gray-600 font-medium mb-4">
      Capture/Upload referral letter or photo
    </h3>

    <div
      v-if="existingUploadedDocuments.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <!-- Left Column: Patient Information -->
      <div>
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          Existing Documents
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div
            v-for="doc in existingUploadedDocuments"
            :key="doc.DocumentId"
            class="border rounded-lg shadow-sm p-4 flex flex-col items-center justify-center bg-white"
          >
            <!-- File Preview (Image or Placeholder) -->
            <div class="mb-2">
              <span v-if="doc.FileType.startsWith('image/')">
                <img
                  :src="doc.FileURL"
                  :alt="doc.FileName"
                  class="w-32 h-32 object-cover rounded"
                />
              </span>
              <span v-else>
                <div
                  class="w-32 h-32 flex items-center justify-center bg-gray-100 rounded"
                >
                  <span class="text-gray-500 text-sm">No Preview</span>
                </div>
              </span>
            </div>

            <!-- File Name -->
            <p class="text-center text-sm font-medium mb-2">
              {{ doc.FileName }}
            </p>

            <!-- Open File Button -->
            <button
              @click="openFile(doc.FileURL)"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Open File
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap gap-4">
      <!-- File upload area -->
      <div
        class="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center w-48 h-48 cursor-pointer"
        @click="triggerFileInput"
        @dragover.prevent
        @drop.prevent="handleFileDrop"
      >
        <input
          type="file"
          ref="fileInput"
          class="hidden"
          @change="handleFileSelect"
          accept="image/*,.pdf"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-10 w-10 text-gray-400 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p class="text-sm text-gray-500">
          Click here or drag and drop your files here
        </p>
      </div>

      <!-- Uploaded document previews -->
      <div
        v-for="(doc, index) in uploadedDocuments"
        :key="index"
        class="relative"
      >
        <img
          :src="doc.preview"
          alt="Uploaded document"
          class="w-48 h-48 object-cover rounded-lg"
        />
        <button
          @click="removeDocument(index)"
          class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AWS from "aws-sdk"; // Import AWS SDK
// import settings from '../../../../config/settings.json'; // Import settings file

export default {
  name: "PatientDocumentUploadForm", // Component name
  props: {
    formData: {
      type: Object,
      required: true,
    },
    existingUploadedDocuments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      patientRegForm: { ...this.formData },
      uploadedDocuments: [],
      selectedFile: null,
      uploading: true,
    };
  },
  methods: {
    updateForm() {
      this.$emit("update:formData", this.patientRegForm);
      console.log(this.patientRegForm); // Emit updated form data to parent
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.selectedFile = event.target.files[0];
      this.uploadFile();
    },
    handleFileDrop(event) {
      this.addDocuments(event.dataTransfer.files);
    },
    async uploadFile() {
      if (!this.selectedFile) {
        alert("No file selected.");
        return;
      }

      this.$emit("uploading", true);
      console.log("selectedFile", this.selectedFile);
      // Configure AWS SDK
      const s3 = new AWS.S3({
        endpoint: process.env.VUE_APP_DO_ENDPOINT,
        signatureVersion: "v4",
        accessKeyId: process.env.VUE_APP_DO_ACCESS_KEY,
        secretAccessKey: process.env.VUE_APP_DO_SECRET_KEY,
        region: process.env.VUE_APP_DO_REGION,
      });

      const params = {
        Bucket: "jiji-bucket", // Replace with your Space name
        Key: this.selectedFile.name,
        Body: this.selectedFile,
        ACL: "public-read", // Make the file public if needed
        ContentType: this.selectedFile.type,
      };

      try {
        const result = await s3.upload(params).promise();

        this.patientRegForm.patient.uploadedDocuments.push({
          fileName: this.selectedFile.name,
          fileType: this.selectedFile.type,
          fileUrl: result.Location,
        });
        this.uploadedDocuments.push({
          preview: URL.createObjectURL(this.selectedFile),
          url: result.Location,
        });
        this.$toast.success("File uploaded successfully.");
        this.$emit("uploading", false);
        console.log(result);
      } catch (error) {
        console.error(error);
        this.$toast.error(
          "Unable to upload file, please check your internet connection."
        );
        this.$emit("uploading", false);
      }
    },
  },
};
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
